/**
 * 陪练结果卡(V2 批次C)—— 三个陪练页共用同一张卡:
 * wecom-h5 语音页 / wecom-h5 文本页(浅色) + advisor-h5 文本页(深色玻璃)。
 *
 * 在这之前三处各写一遍「大号靛蓝数字 + 一段反馈」,改一处漏两处,也没有档位概念。
 *
 * 设计取舍见 docs/2026-08-06-coaching-voice-v2-START.md §4,五条硬规矩都在这里落地:
 * ①低置信度不撒花 ②评估失败不撒花 ③只撒一次(轮询 2s 一跳,不锁就是每 2 秒撒一次)
 * ④粒子层 pointer-events:none 且到点摘干净 ⑤减弱动效全档降级
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, SpinLoading } from "antd-mobile";
import { celebrationOf, gradeOf, type ScoreGrade } from "./scoreGrade";
import "./scoreCelebration.css";

export interface ScoreResultCardProps {
  status: "evaluating" | "completed" | "failed";
  score: number | null;
  /** 教练反馈(completed)或失败原因(failed)。 */
  feedback: string | null;
  lowConfidence?: boolean;
  /**
   * 做得好的 / 下次改这两点(V33)。练完唯一有指导价值的东西 —— 在这之前卡上只有一个分数
   * 和一句「对话结果:pending,客户购买意向:36/100」。空数组=这一局 AI 没给,整块不出现。
   */
  highlights?: string[];
  improvements?: string[];
  tone?: "light" | "dark";
  onBack: () => void;
  backLabel: string;
  /** 待加强档的「再练一次」。不传 = 不给重练入口(如任务制已及格,不许刷分)。 */
  onRetry?: () => void;
}

interface Particle {
  id: number;
  left: number;
  dx: number;
  dy: number;
  rot: number;
  delay: number;
  color: string;
  size: number;
}

/** 饱和度压到 80% 左右 —— 门店灯光下不刺眼。 */
const PALETTE: Record<ScoreGrade, string[]> = {
  excellent: ["#F5A623", "#FFD980", "#FF8A6B", "#5BC8FF"],
  good: ["#5B6CFF", "#9AA7FF", "#FFFFFF"],
  pass: [],
  weak: [],
};

const TONE_TEXT = {
  light: { title: "text-slate-900", body: "text-slate-600", muted: "text-slate-500" },
  dark: { title: "text-white", body: "text-white/80", muted: "text-white/60" },
};

const GRADE_ACCENT: Record<ScoreGrade, { light: string; dark: string; chip: string }> = {
  excellent: { light: "#D98806", dark: "#FFC760", chip: "bg-amber-50 text-amber-700" },
  good: { light: "#4655E0", dark: "#A9B4FF", chip: "bg-indigo-50 text-indigo-700" },
  pass: { light: "#0E9F5D", dark: "#6BE3AC", chip: "bg-emerald-50 text-emerald-700" },
  // 待加强用中性石板色,不用红:门店里手机会被同事看见,红色等于公开处刑
  weak: { light: "#475569", dark: "#CBD5E1", chip: "bg-slate-100 text-slate-600" },
};

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
  } catch {
    return false;
  }
}

/** 低端机砍半 —— 60 个 DOM 粒子在 4 核以下的安卓上会掉帧。 */
function scaleForDevice(n: number): number {
  const cores = (navigator as { hardwareConcurrency?: number }).hardwareConcurrency;
  return cores !== undefined && cores <= 4 ? Math.ceil(n / 2) : n;
}

function makeParticles(count: number, grade: ScoreGrade, durationMs: number): Particle[] {
  const colors = PALETTE[grade];
  if (!colors.length) return [];
  const spark = grade === "good";
  return Array.from({ length: count }, (_, i) => {
    // 两个发射点(卡片顶部左右),扇形抛出;星光档只在中间小范围向上飘
    const fromLeft = i % 2 === 0;
    const spread = spark ? 60 : 180;
    return {
      id: i,
      left: spark
        ? 42 + Math.random() * 16
        : fromLeft
          ? 18 + Math.random() * 12
          : 70 + Math.random() * 12,
      dx: (Math.random() - 0.5) * spread + (spark ? 0 : fromLeft ? 40 : -40),
      dy: spark ? -70 - Math.random() * 60 : 180 + Math.random() * 220,
      rot: spark ? 0 : (Math.random() - 0.5) * 900,
      delay: Math.random() * (durationMs * 0.25),
      color: colors[i % colors.length],
      size: 0.8 + Math.random() * 0.6,
    };
  });
}

/** 从教练反馈里数出「可优化的点」有几条;数不出返回 0 → 文案退化成不带数字的句子。 */
export function countFeedbackPoints(feedback: string | null): number {
  if (!feedback) return 0;
  const numbered = feedback.match(/(?:^|\n)\s*[1-9][.、)）]/g);
  if (numbered && numbered.length >= 2) return numbered.length;
  const bulleted = feedback.match(/(?:^|\n)\s*[-·•]/g);
  if (bulleted && bulleted.length >= 2) return bulleted.length;
  return 0;
}

/** 分数从 0 滚上来。待加强档不滚(看数字慢慢爬到 45 分是一种凌迟)。 */
function useCountUp(target: number | null, enabled: boolean): number | null {
  const [value, setValue] = useState<number | null>(target === null ? null : enabled ? 0 : target);
  useEffect(() => {
    if (target === null) {
      setValue(null);
      return;
    }
    if (!enabled) {
      setValue(target);
      return;
    }
    const steps = 30;
    let i = 0;
    setValue(0);
    const timer = setInterval(() => {
      i += 1;
      // ease-out:开头快尾巴慢,和徽章弹入的节奏对得上
      const p = 1 - Math.pow(1 - i / steps, 3);
      setValue(Math.round(target * p));
      if (i >= steps) {
        setValue(target);
        clearInterval(timer);
      }
    }, 30);
    return () => clearInterval(timer);
  }, [target, enabled]);
  return value;
}

/** 一段带色条的要点列表(做得好的 / 下次改这两点)。左色条是唯一的区分手段,不靠文字颜色 —— 深浅两皮都得读得出。 */
function FeedbackSection({
  label,
  lines,
  tone,
  accent,
}: {
  label: string;
  lines: string[];
  tone: "light" | "dark";
  accent: string;
}) {
  const text = TONE_TEXT[tone];
  return (
    <div
      className={`rounded-lg p-3 border-l-[3px] ${tone === "dark" ? "bg-white/5" : "bg-slate-50"}`}
      style={{ borderLeftColor: accent }}
    >
      <p className="text-2xs mb-1 font-medium" style={{ color: accent }}>
        {label}
      </p>
      <ul className={`space-y-1 text-xs leading-relaxed ${text.title}`}>
        {lines.map((line) => (
          <li key={line} className="flex gap-1.5">
            <span aria-hidden="true" className={text.muted}>
              ·
            </span>
            <span className="flex-1">{line}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ScoreResultCard({
  status,
  score,
  feedback,
  lowConfidence = false,
  highlights = [],
  improvements = [],
  tone = "light",
  onBack,
  backLabel,
  onRetry,
}: ScoreResultCardProps) {
  const { t } = useTranslation("coaching");
  const grade = gradeOf(score);
  const reduceMotion = useMemo(() => prefersReducedMotion(), []);
  const spec = grade
    ? celebrationOf(grade, lowConfidence, reduceMotion)
    : {
        particles: 0,
        shape: "none" as const,
        countUp: false,
        checkmark: false,
        retryPrimary: false,
        durationMs: 0,
      };

  const [particles, setParticles] = useState<Particle[]>([]);
  const shownScore = useCountUp(status === "completed" ? score : null, spec.countUp);

  /**
   * 硬规矩③「只撒一次」靠的是 **deps 只在评分落定那一刻变一次**(status→completed、
   * grade null→档位),轮询每 2s 引发的 re-render 不会重跑本 effect;粒子被摘掉后
   * 也不会被重新填回去。
   *
   * 这里**故意不加 playedRef 之类的一次性锁**:实测加了也杀不掉任何变异(deps 已经
   * 保证了),反而会在 React StrictMode 的 effect 双跑里把撒花整个吞掉。
   */
  useEffect(() => {
    if (status !== "completed" || !grade) return;
    if (spec.particles <= 0) return;
    setParticles(makeParticles(scaleForDevice(spec.particles), grade, spec.durationMs));
    // 到点把粒子从 DOM 摘掉,不留 opacity:0 的僵尸节点
    const timer = setTimeout(() => setParticles([]), spec.durationMs + 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, grade]);

  const text = TONE_TEXT[tone];
  const accent = grade ? GRADE_ACCENT[grade] : GRADE_ACCENT.weak;
  const accentColor = tone === "dark" ? accent.dark : accent.light;
  const points = countFeedbackPoints(feedback);
  const shell =
    tone === "dark"
      ? "av-glass rounded-xl p-4 space-y-3"
      : "mx-2 p-4 rounded-xl bg-white border border-slate-200 shadow-sm space-y-3";

  return (
    <div className={`${shell} sc-card-in`}>
      {particles.length > 0 && (
        <div className="sc-particles" aria-hidden="true" data-testid="score-particles">
          {particles.map((p) => (
            <span
              key={p.id}
              data-particle=""
              data-shape={spec.shape}
              className="sc-particle"
              style={
                {
                  left: `${p.left}%`,
                  "--sc-dx": `${p.dx}px`,
                  "--sc-dy": `${p.dy}px`,
                  "--sc-rot": `${p.rot}deg`,
                  "--sc-delay": `${p.delay}ms`,
                  "--sc-dur": `${spec.durationMs}ms`,
                  "--sc-color": p.color,
                  transform: `scale(${p.size})`,
                } as React.CSSProperties
              }
            />
          ))}
        </div>
      )}

      <p className={`text-sm font-semibold text-center ${text.title}`}>
        {status === "completed" && grade
          ? t(`wecomChat.grade.${grade}.title`)
          : t("wecomChat.endTitle")}
      </p>

      {status === "evaluating" && (
        <div className="flex flex-col items-center gap-2 py-2">
          <SpinLoading color="primary" />
          <p className={`text-xs ${text.muted}`}>{t("wecomChat.evaluating")}</p>
        </div>
      )}

      {status === "completed" && grade && (
        <>
          {/* 待加强档:分数让位,先说问题(信息顺序反转) */}
          {spec.retryPrimary ? (
            <div className={`text-center text-xs ${text.body} leading-relaxed`}>
              {points > 0
                ? t("wecomChat.grade.weak.desc", { n: points })
                : t("wecomChat.grade.weak.descPlain")}
            </div>
          ) : null}

          <div className="text-center">
            {spec.checkmark && (
              <svg
                data-checkmark=""
                viewBox="0 0 24 24"
                className="mx-auto mb-1 h-7 w-7"
                aria-hidden="true"
              >
                <path
                  className="sc-check-path"
                  d="M4 12.5l5 5 11-11"
                  fill="none"
                  stroke={accentColor}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            <span
              className={`font-bold tabular-nums ${spec.retryPrimary ? "text-2xl" : "text-4xl"}`}
              style={{ color: accentColor }}
            >
              {shownScore ?? score}
            </span>
            <span className={`text-sm ml-1 ${text.muted}`}>{t("wecomChat.scoreSuffix")}</span>
            <div className="mt-1.5 flex items-center justify-center gap-1.5">
              <span
                className={`sc-badge-pop inline-block px-2 py-0.5 rounded text-2xs ${
                  tone === "dark" ? "bg-white/10" : accent.chip
                }`}
                style={tone === "dark" ? { color: accentColor } : undefined}
              >
                {t(`wecomChat.grade.${grade}.badge`)}
              </span>
              {lowConfidence && (
                <span className="inline-block px-2 py-0.5 rounded bg-amber-50 text-amber-700 text-2xs">
                  {t("wecomChat.lowConfidence")}
                </span>
              )}
            </div>
          </div>

          {!spec.retryPrimary && (
            <p className={`text-center text-xs ${text.body} leading-relaxed`}>
              {points > 0
                ? t(`wecomChat.grade.${grade}.desc`, { n: points })
                : t(`wecomChat.grade.${grade}.descPlain`)}
            </p>
          )}
        </>
      )}

      {/*
        两段反馈排在教练总评**之前**:总评是一句概括,这两段才是可执行的。
        「下次改」用中性琥珀不用红 —— 门店里手机会被同事看见,红色等于公开处刑(同 weak 档的取舍)。
      */}
      {status === "completed" && highlights.length > 0 && (
        <FeedbackSection
          label={t("wecomChat.highlights")}
          lines={highlights}
          tone={tone}
          accent={tone === "dark" ? "#6BE3AC" : "#0E9F5D"}
        />
      )}
      {status === "completed" && improvements.length > 0 && (
        <FeedbackSection
          label={t("wecomChat.improvements")}
          lines={improvements}
          tone={tone}
          accent={tone === "dark" ? "#FFC760" : "#B7791F"}
        />
      )}

      {status === "completed" && feedback && (
        <div className={`rounded-lg p-3 ${tone === "dark" ? "bg-white/5" : "bg-slate-50"}`}>
          <p className={`text-2xs mb-1 ${text.muted}`}>{t("wecomChat.coachFeedback")}</p>
          <p className={`text-xs leading-relaxed whitespace-pre-wrap ${text.title}`}>{feedback}</p>
        </div>
      )}

      {status === "failed" && (
        <p className="text-xs text-red-600 text-center leading-relaxed">
          {feedback ?? t("wecomChat.evalFailed")}
        </p>
      )}

      {/* 待加强档:「再练一次」是主按钮,返回退到次要 */}
      {spec.retryPrimary && onRetry ? (
        <div className="space-y-2 mt-2">
          <Button block color="primary" size="small" onClick={onRetry}>
            {t("wecomChat.grade.weak.retry")}
          </Button>
          <Button block fill="none" size="small" onClick={onBack}>
            <span className={text.muted}>{backLabel}</span>
          </Button>
        </div>
      ) : (
        <Button block color="primary" size="small" onClick={onBack} className="mt-2">
          {backLabel}
        </Button>
      )}
    </div>
  );
}
